/**
 * 页内 Tab 注册表：把「页面内部的页签」也纳入导航/搜索（模块功能地图）。
 * 单一真源仍是各页面（useBccTabUrl 的 key），此表是「给导航用的投影」，key 必须与页面一致；
 * 目标路由 = feature.route + `?tab=<key>`（页面用 useBccTabUrl 读取 ?tab=）。
 * 仅登记「值得被搜索/直达」的页签；纯技术性页签可不登记。新增/改动页签时同步此表。
 */

import type { RequiredPermission } from './access';

export interface NavTab {
  key: string;
  title: string;
  /**
   * 该页签自身的权限码（缺省=继承所属 Feature 的权限码）；码组＝「任一命中」。
   * 只有「设置 Landing」这类**页签权限码与入口不一致**的页面才需要登记：
   * 入口是「任一子码」，页签各按自己的子码裁剪（如呼入设置里坐席只见黑名单）。
   */
  permission?: RequiredPermission;
}

/** feature id → 该页页内 tab 列表（顺序同页面） */
export const NAV_TABS: Record<string, NavTab[]> = {
  // 坐席（用户与组织）：档案与技能组同页；技能组为多域复用资源但按「坐席菜单里的资源」落位
  'identity.agents': [
    { key: 'agents', title: '坐席档案' },
    { key: 'skill-groups', title: '技能组', permission: 'inbound.skill-groups' },
  ],
  // 进线路由（子项各有独立菜单码：主管持 inbound.ivr → 只看到 IVR 菜单 Tab；呼叫路由为 admin）
  'telephony.routing': [
    { key: 'routes', title: '呼叫路由', permission: 'inbound.routes' },
    { key: 'ivr', title: 'IVR 菜单', permission: 'inbound.ivr' },
  ],
  // 待办中心（顶部锚点，ModuleMapModal 按 route 反查 feature 后展示）：三页签与顶栏通知铃一一对应。
  // 历史沿革：这里曾登记过 `{ key: 'kb', title: '知识库运营' }`（页面早已删掉该页签，留着会让
  // Ctrl+M 列出点了没落点的死页签）；也曾登记过五个**状态**页签（待审批/已通过/…）——状态已收成
  // 页内下拉框（?state=），不再是页面页签，故从本表移除；知识侧待办一律去「知识待办」。
  'workflow.inbox': [
    { key: 'todo', title: '待办' },
    { key: 'notice', title: '通知' },
    { key: 'announce', title: '公告' },
  ],
  // 机器人意图（通用规则词表并入本页后，四个页签都在这张表里）
  'bot.intents': [
    { key: 'vocab', title: '意图词表' },
    { key: 'ruleword', title: '通用规则词表', permission: 'bot.ruleword' },
    { key: 'stats', title: '统计分析' },
    { key: 'test', title: '意图测试台' },
    { key: 'shadow', title: 'A/B 对照' },
  ],
  // 语音行为默认（配置项按语音链路阶段分页签，页签内两列）
  'bot.voice.defaults': [
    { key: 'backchannel', title: '静音垫' },
    { key: 'silence', title: '静默提示' },
    { key: 'barge', title: '打断与垫话' },
    { key: 'transfer', title: '转人工确认' },
    { key: 'asr', title: 'ASR 结果修正' },
    { key: 'slow', title: '适老慢速播报' },
  ],
  // 智能质检（各一级组页内 tab；质检详情已下线「抽检」前端入口；流程质检并入质检详情）
  'quality.sampling': [
    { key: 'reports', title: '报告' },
    { key: 'appeals', title: '申诉' },
    { key: 'flow-reports', title: '流程质检', permission: 'quality.flow-reports' },
    { key: 'samples', title: '样本回流' },
  ],
  // 规则（跨集合总览 + 完整配置入口）/ 规则集（规则明细从其抽屉进）
  'quality.rules': [
    { key: 'rules', title: '规则' },
    { key: 'rule-sets', title: '规则集' },
  ],
  // 质检任务（任务列表 / 任务配置）
  // 质检任务只剩任务列表（任务配置已下线），单子分组不渲染 Tab 栏
  'quality.jobs': [{ key: 'jobs', title: '任务列表' }],
  // 质检分析已合并为单页（综合报告 + 统计），无页内 Tab，故此处不登记
  // 知识库
  // WeKnora 三个入口（知识问答 / 知识管理 / 问答历史）都是外部系统的 iframe 壳，页内没有本系统的页签，故本表不登记。
  // 原「知识待办」「知识分析」「知识库设置」「召回验证」四页及其页签已随自建问答库与全文库整体下线，
  // 对应登记项一并删除（页面与菜单码同时下线，留在这里会让功能地图指向不存在的页）。
  // 工单设置
  'ticketing.settings': [
    { key: 'fields', title: '工单字段', permission: 'ticketing.settings.fields' },
    { key: 'routing', title: '派单规则', permission: 'ticketing.settings.routing' },
    { key: 'templates', title: '工单创建模板', permission: 'ticketing.settings.templates' },
    { key: 'approval', title: '审核流程', permission: 'ticketing.settings.approval' },
    { key: 'auto-create', title: 'AI 自动建单', permission: 'ticketing.settings.auto-create' },
  ],
  // 运营监控
  'operations.cockpit': [
    { key: 'cockpit', title: '运营驾驶舱' },
    { key: 'agents', title: '坐席状态' },
    { key: 'queues', title: '队列实况' },
  ],
  // 运营设置
  'operations.settings': [
    { key: 'alerts', title: '驾驶舱预警', permission: 'operations.settings.alerts' },
    { key: 'agents', title: '坐席运营', permission: 'operations.settings.agents' },
  ],
  // 排班
  'operations.shifts': [
    { key: 'calendar', title: '排班日历' },
    { key: 'templates', title: '班次模板' },
  ],
  // 客户洞察设置
  'customer-insights.settings': [
    { key: 'satisfaction', title: '满意度设置', permission: 'customer-insights.settings.satisfaction' },
    { key: 'voc-labels', title: 'VOC 标签管理', permission: 'customer-insights.settings.voc-labels' },
  ],
  // 报表中心（三个一级组，各自页内 tab）
  'analytics.report.core': [
    { key: 'summary', title: '统计汇总' },
    { key: 'agents', title: '坐席工作量' },
    { key: 'queues', title: '队列报表' },
    { key: 'satisfaction', title: '满意度' },
  ],
  'analytics.report.ai': [
    { key: 'ai', title: 'AI 机器人' },
    { key: 'aiEfficiency', title: 'AI 效能' },
    { key: 'aiObservability', title: 'AI 可观测' },
    { key: 'chatBots', title: '聊天机器人' },
  ],
  'analytics.report.service': [
    { key: 'sla', title: 'SLA 报表' },
    { key: 'agentHours', title: '坐席工时' },
    { key: 'outbound', title: '外呼效果' },
  ],
  // 通信设置
  'telephony.settings': [
    { key: 'sip', title: 'SIP 参数', permission: 'telephony.sip' },
    { key: 'media', title: '呼叫与媒体', permission: 'telephony.capacity' },
    { key: 'whitelist', title: '电话白名单', permission: 'telephony.settings' },
  ],
  // 集成
  // 「开放平台」页签随开放平台整体下线（应用/密钥管理页与接口一并摘除），故此处不再登记。
  // 外部数据源
  'integration.data-sources': [
    { key: 'sources', title: '数据源' },
    { key: 'calls', title: '调用记录' },
  ],
  // AI 能力（供应商）
  // 六个能力页签同属一页：录音文件识别是 ASR 的离线（录音整段）对偶，随能力清单落在这里
  // （原「AI 能力 › 平台设置」页只装 VC 与录音文件识别两项；该页已撤，VC 能力整体删除）
  'ai.providers': [
    { key: 'asr', title: 'ASR 语音识别' },
    { key: 'tts', title: 'TTS 语音合成' },
    { key: 'llm', title: 'LLM 大模型' },
    { key: 'embedding', title: 'Embedding 向量化' },
    { key: 'fileasr', title: '录音文件识别' },
  ],
  // 调用监控：同一批 AI 调用的两种看法——聚合看板与逐次明细
  'ai.usage': [
    { key: 'board', title: '看板' },
    { key: 'calls', title: '调用明细' },
  ],
  // 运营与成本
  'ai.cost': [
    { key: 'cost', title: '成本单价' },
    { key: 'alert', title: '异常告警' },
    { key: 'configcheck', title: '配置完整性' },
  ],
  // 故障诊断
  'diagnostics.diag': [
    { key: 'timeline', title: '全链路时间线' },
    { key: 'fs', title: 'FS 通道' },
    { key: 'agent', title: '坐席状态' },
    { key: 'queue', title: '队列' },
    { key: 'transcript', title: '转写' },
    { key: 'ailogs', title: 'AI 日志' },
    { key: 'quality', title: '质检' },
    { key: 'fsmeta', title: 'FS 概览' },
  ],
};

/** 取某 feature 的页内 tab（无则空数组） */
export function tabsOf(featureId: string): NavTab[] {
  return NAV_TABS[featureId] ?? [];
}
